import { useMemo } from 'react';

import type { DocNode } from '@atlaskit/adf-schema';
import { isStoreAssistant } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useChatMessages } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';

import { extractPlainTextFromAdf } from '../../common/utils/content-features/extractPlainTextFromAdf';
import { getMessageHistory } from '../chat-store/getMessageHistory';
import { removeSensitiveData } from '../chat-store/removeSensitiveData';
import { useChatStore } from '../chat-store/useChatStore';

const MAX_HISTORY_CHARS = 30000;

/**
 * Removes a single named field from an item, regardless of nesting (handles
 * StreamResponse format where the field lives at item.message.message.<field>
 * and the flat format where it lives at item.<field>).
 */
type NestedMessage = {
	message?: {
		message?: Record<string, unknown>;
	};
} & Record<string, unknown>;

const omitField = (item: unknown, field: string): unknown => {
	if (!item || typeof item !== 'object') {
		return item;
	}

	const record = item as NestedMessage;

	// StreamResponse format: item.message.message.<field>
	if (record.message?.message && field in record.message.message) {
		const { [field]: _omitted, ...rest } = record.message.message;
		return {
			...record,
			message: { ...record.message, message: rest },
		};
	}

	// Flat format: item.<field>
	if (field in record) {
		const { [field]: _omitted, ...rest } = record;
		return rest;
	}

	return item;
};

/**
 * Fields stripped, in order of least to most useful for debugging. Strip cascade
 * stops as soon as the payload fits under MAX_HISTORY_CHARS — so normal-sized
 * conversations have nothing removed, and only oversized payloads lose data.
 */
const TRIMMABLE_FIELDS = [
	'minion_outputs',
	'orchestration_graphs',
	'subagent_invocations',
	'agent_user_context',
	'appendices',
	'sources',
	'actions',
	'usage_data',
	'memory',
] as const;

/**
 * Converts ADF content to plain text within a message, preserving all other fields.
 * Only used as a last resort when dropping messages alone isn't enough.
 */
const flattenAdfContent = (item: unknown): unknown => {
	const toText = (content: unknown): unknown => {
		if (typeof content === 'string') {
			return content;
		}
		if (content && typeof content === 'object' && (content as DocNode).type === 'doc') {
			return extractPlainTextFromAdf(content as DocNode);
		}
		return content;
	};

	if (!item || typeof item !== 'object') {
		return item;
	}

	const record = item as NestedMessage & { content?: unknown };

	if (record.message?.message?.content !== undefined) {
		return {
			...record,
			message: {
				...record.message,
				message: { ...record.message.message, content: toText(record.message.message.content) },
			},
		};
	}
	if (record.content !== undefined) {
		return { ...record, content: toText(record.content) };
	}
	return item;
};

export const useMessageHistory = ({
	canAttachMessages,
	answerId,
}: {
	canAttachMessages: boolean;
	answerId?: string;
}): unknown[] => {
	const [{ currentConversationId }] = useChatStore();
	const [messages] = useChatMessages({ conversationId: currentConversationId });
	const history = useMemo(() => {
		if (!canAttachMessages) {
			return [];
		}

		let items: unknown[] = getMessageHistory(messages, answerId, 5)
			.filter((storeChatItem) => 'content' in storeChatItem)
			.map((storeChatItem) => {
				if (isStoreAssistant(storeChatItem)) {
					return removeSensitiveData(storeChatItem).content;
				}
				return (storeChatItem as { content: unknown }).content;
			});

		if (JSON.stringify(items).length <= MAX_HISTORY_CHARS) {
			return items;
		}

		const allItems = items;
		for (let numToKeep = allItems.length; numToKeep >= 2; numToKeep--) {
			items = allItems.slice(-numToKeep);
			if (JSON.stringify(items).length <= MAX_HISTORY_CHARS) {
				return items;
			}
		}

		for (const field of TRIMMABLE_FIELDS) {
			items = items.map((item) => omitField(item, field));
			if (JSON.stringify(items).length <= MAX_HISTORY_CHARS) {
				return items;
			}
		}

		if (JSON.stringify(items).length > MAX_HISTORY_CHARS) {
			items = items.map(flattenAdfContent);
			const maxPerMsg = Math.max(200, Math.floor(MAX_HISTORY_CHARS / items.length) - 500);
			items = items.map((item: unknown) => {
				if (!item || typeof item !== 'object') {
					return item;
				}

				const record = item as NestedMessage & { content?: unknown };

				if (
					typeof record.message?.message?.content === 'string' &&
					record.message.message.content.length > maxPerMsg
				) {
					return {
						...record,
						message: {
							...record.message,
							message: {
								...record.message.message,
								content: `${record.message.message.content.slice(0, maxPerMsg)}... [truncated]`,
							},
						},
					};
				}
				if (typeof record.content === 'string' && record.content.length > maxPerMsg) {
					return {
						...record,
						content: `${record.content.slice(0, maxPerMsg)}... [truncated]`,
					};
				}
				return item;
			});
		}

		return items;
	}, [canAttachMessages, messages, answerId]);

	return history;
};
